/**
 * Ctrl+K global search palette (Phase 2). Searches campaign state only for
 * now; Phase 3 will append source-content candidates (techniques/feats/rules)
 * without changing this UI — see search.ts.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useT } from '@/i18n';
import { closeSearch, navigate, selectEntity, useAppState } from '@/state/store';
import { searchCampaign, type SearchResult } from '@/ui/search';

export default function SearchPalette(): ReactElement | null {
  const { campaign, ui } = useAppState();
  const t = useT();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(
    () => (campaign ? searchCampaign(campaign, query, ui.locale) : []),
    [campaign, query, ui.locale],
  );

  // Reset the query each time the palette opens.
  useEffect(() => {
    if (ui.searchOpen) {
      setQuery('');
      setActive(0);
      // Focus after paint so the overlay exists.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [ui.searchOpen]);

  if (!ui.searchOpen) return null;

  const pick = (result: SearchResult): void => {
    closeSearch();
    navigate(result.route);
    selectEntity(result.id);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeSearch();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      const result = results[active];
      if (result) {
        e.preventDefault();
        pick(result);
      }
    }
  };

  return (
    <div
      className="palette-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeSearch();
      }}
    >
      <div className="palette" role="dialog" aria-modal="true" aria-label={t('search.title')}>
        <input
          ref={inputRef}
          type="text"
          className="palette-input"
          placeholder={t('search.placeholder')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
        {query.trim() !== '' && results.length === 0 ? (
          <p className="muted palette-empty">{t('search.noResults')}</p>
        ) : results.length > 0 ? (
          <ul className="palette-results">
            {results.map((result, index) => (
              <li key={`${result.kind}:${result.id}`}>
                <button
                  type="button"
                  className={`palette-item${index === active ? ' active' : ''}`}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => pick(result)}
                >
                  <span className="chip">{t(`search.kind.${result.kind}`)}</span>
                  <span className="palette-title">{result.title}</span>
                  {result.snippet ? (
                    <span className="palette-snippet">{result.snippet}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted palette-empty">{t('search.hint')}</p>
        )}
        <div className="palette-footer">
          <span>
            <kbd className="kbd">↑</kbd>
            <kbd className="kbd">↓</kbd> {t('search.navigate')}
          </span>
          <span>
            <kbd className="kbd">Enter</kbd> {t('search.open')}
          </span>
          <span>
            <kbd className="kbd">Esc</kbd> {t('search.close')}
          </span>
        </div>
      </div>
    </div>
  );
}
